import type { GenerationResult, ValidationReport, ValidationIssue } from '../types';

/**
 * Combine one or more generated entities into the canonical Schema.org
 * shape: a single object when there's one entity, a {@context, @graph: [...]}
 * wrapper when there are multiple.
 *
 * Each entity in the graph gets an @id derived from sourceUrl + type if it
 * doesn't already have one, so other entities can cross-reference it (e.g.,
 * Article.publisher = { @id: ".../#organization" }).
 */
export function buildGraph(generations: GenerationResult[]): Record<string, unknown> {
  if (generations.length === 0) return { '@context': 'https://schema.org' };
  if (generations.length === 1) {
    return ensureContextAndId(generations[0].jsonLd, generations[0].type, generations[0].sourceUrl);
  }

  // @graph mode: hoist a single top-level @context, strip per-entity @contexts.
  const sourceUrl = generations[0].sourceUrl;
  const graph = generations.map((g) => {
    const node = ensureContextAndId(g.jsonLd, g.type, g.sourceUrl);
    const { '@context': _ctx, ...rest } = node;
    return rest;
  });

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
    ...(sourceUrl ? { url: sourceUrl } : {}),
  };
}

function ensureContextAndId(
  obj: Record<string, unknown>,
  typeName: string,
  sourceUrl: string,
): Record<string, unknown> {
  const result = { ...obj };
  if (result['@context'] !== 'https://schema.org') {
    result['@context'] = 'https://schema.org';
  }
  if (!result['@id'] && sourceUrl) {
    try {
      const u = new URL(sourceUrl);
      u.hash = `#${typeName.toLowerCase()}`;
      result['@id'] = u.toString();
    } catch {
      // ignore — sourceUrl wasn't a valid URL
    }
  }
  return result;
}

/**
 * Combine validation reports across multiple entities. Issues are prefixed
 * with the entity's type so the user knows which one to fix.
 */
export function combineValidation(generations: GenerationResult[]): ValidationReport {
  if (generations.length <= 1) {
    return generations[0]?.validation ?? { ok: true, issues: [], errors: 0, warnings: 0, infos: 0 };
  }
  const issues: ValidationIssue[] = [];
  for (const g of generations) {
    for (const issue of g.validation.issues) {
      issues.push({
        ...issue,
        field: `${g.type}.${issue.field}`,
      });
    }
  }
  return {
    ok: issues.every((i) => i.severity !== 'error'),
    issues,
    errors: issues.filter((i) => i.severity === 'error').length,
    warnings: issues.filter((i) => i.severity === 'warning').length,
    infos: issues.filter((i) => i.severity === 'info').length,
  };
}
